import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, Mail, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { identifyUser } from "@/lib/klaviyo";

interface KlaviyoFormProps {
  listId?: string;
  source?: string;
  className?: string;
  compact?: boolean;
  onSuccess?: (discountCode: string) => void;
}

// Fixed 10% discount code - verified in Shopify price rules
const WELCOME_DISCOUNT_CODE = "SPIN10%";

// Supabase Edge Function URL
const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/klaviyo-subscribe`;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export const KlaviyoForm = ({ 
  listId, 
  source = "website",
  className = "",
  compact = false,
  onSuccess
}: KlaviyoFormProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    if (!discountCode) return;
    try {
      await navigator.clipboard.writeText(discountCode);
      setCopied(true);
      toast.success("Kopierad!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kunde inte kopiera");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error("Ange en giltig e-postadress");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Ange en giltig e-postadress");
      return;
    }

    setIsLoading(true);

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (SUPABASE_ANON_KEY) {
        headers["apikey"] = SUPABASE_ANON_KEY;
        headers["Authorization"] = `Bearer ${SUPABASE_ANON_KEY}`;
      }

      const response = await fetch(EDGE_FUNCTION_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          listId,
          source,
        }),
      });

      const raw = await response.text();
      const data = raw ? (() => {
        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      })() : null;

      if (!response.ok) {
        const message =
          (data && typeof data === "object" && "error" in data && typeof (data as any).error === "string"
            ? (data as any).error
            : raw) || `Request failed (${response.status})`;
        throw new Error(message);
      }
      
      // Identify user in Klaviyo for tracking
      const normalizedEmail = email.trim().toLowerCase();
      identifyUser(normalizedEmail, { 
        source: source,
        subscribed_at: new Date().toISOString()
      });
      
      // Show discount code immediately
      setDiscountCode(WELCOME_DISCOUNT_CODE);
      setIsSubscribed(true);
      onSuccess?.(WELCOME_DISCOUNT_CODE);
      
      toast.success("🎉 Din rabattkod är klar!", {
        description: `Använd ${WELCOME_DISCOUNT_CODE} i kassan för 10% rabatt`,
        position: "top-center",
        duration: 8000
      });
      setEmail("");
    } catch (error: any) {
      console.error("Klaviyo subscription error:", error);
      toast.error("Något gick fel", {
        description: (error?.message && String(error.message).slice(0, 140)) || "Försök igen eller kontakta oss",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Success state with visible discount code
  if (isSubscribed && discountCode) {
    return (
      <div className={`p-5 rounded-xl bg-accent/10 border border-accent/30 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full cta-gradient flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-black" />
          </div>
          <div>
            <span className="font-bold text-foreground block">Tack! 🎉</span>
            <p className="text-sm text-muted-foreground">Din rabattkod är redo att användas:</p>
          </div>
        </div>
        
        {/* Discount code display with copy button */}
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-card border-2 border-dashed border-primary/50 rounded-lg px-4 py-3 text-center">
            <span className="text-xl font-bold text-gradient tracking-wider">{discountCode}</span>
          </div>
          <Button
            onClick={copyToClipboard}
            variant="outline"
            size="icon"
            className="h-12 w-12 flex-shrink-0 hover:bg-primary/10"
          >
            {copied ? (
              <Check className="w-5 h-5 text-accent" />
            ) : (
              <Copy className="w-5 h-5" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          ✨ Ange koden i kassan för 10% rabatt på hela köpet
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Din e-postadress"
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-background/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm transition-all"
            disabled={isLoading}
          />
        </div>
        <Button 
          type="submit" 
          disabled={isLoading} 
          className="cta-gradient whitespace-nowrap rounded-xl px-6"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Prenumerera"}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Ange din e-postadress"
            className="w-full pl-12 pr-4 py-4 rounded-xl bg-background/50 border border-border focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            disabled={isLoading}
          />
        </div>
        <Button 
          type="submit" 
          disabled={isLoading} 
          size="lg"
          className="cta-gradient whitespace-nowrap rounded-xl px-8 h-[54px] pulse-cta"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <span className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Få 10% rabatt
            </span>
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        ✨ Genom att prenumerera godkänner du våra villkor. Avsluta när som helst.
      </p>
    </form>
  );
};
